import React from "react";

import { AlertType } from "@constants";
import { Select, Button, Alert } from "@partials";
import { usePagination } from "@hooks";
import { getLocalization } from "@utilities";

import { ArticleListType as Props } from "./types";
import { ItemsList, CategoryFilter } from "./components";

const ArticleList: React.FC<Props> = ({
  api,
  query: defaultQuery,
  items: defaultItems,
  categories,
  pagination: defaultPagination,
  localization,
  cultureCode,
  showCategories,
  showDates,
  errorMessage: defaultErrorMessage,
  iconsList,
  categoryRoot,
}) => {
  const {
    isLoadingMore,
    hasNextItems,
    hasError,
    errorMessage,
    items,
    query,
    setQuery,
    loadNewItemsWithQuery,
    loadNextItems,
  } = usePagination({
    api,
    apiQueryKey: "category",
    defaultQuery,
    defaultItems,
    defaultPagination,
    cultureCode,
    defaultErrorMessage:
      defaultErrorMessage || getLocalization(localization, "listOfArticlesCategory/error"),
  });

  const setUrlSlug = (slug: string, categoryRoot: string): void => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.set("category", slug);
    {
      categoryRoot !== "" &&
        !urlSearchParams.has("categoryroot") &&
        urlSearchParams.set("categoryroot", categoryRoot);
    }
    window.history.replaceState(null, null, `?${urlSearchParams.toString()}`);
  };

  const setNewQueryValue = (value: string): void => {
    /**
     * Save query value which will be used in "loadNextItems" function
     * -> called on "load next" button
     */
    setQuery(value);
    /* Fetch items with query value */
    loadNewItemsWithQuery(value);
    /* Set url param */
    setUrlSlug(value, categoryRoot);
  };

  const handleOnSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const value = event?.target?.value;

    if (!value && value !== "") return;

    setNewQueryValue(value);
  };

  return (
    <div className="container mb-12">
      {showCategories && (
        <div className="row">
          <div className="sm:col-22 sm:offset-1">
            {/* only on stage 1 */}
            <Select
              items={categories}
              labelText={`${getLocalization(
                localization,
                "listOfArticlesCategory/categoriesTitle"
              )}: `}
              name="temata"
              className="flex items-center sm:hidden"
              activeQuery={query}
              handleOnChange={handleOnSelectChange}
            />
            <div className="hidden sm:flex items-center">
              <span className="mr-3 text-2xs">
                {`${getLocalization(localization, "listOfArticlesCategory/categoriesTitle")}: `}
              </span>
              <ul className="flex flex-wrap items-center gap-1">
                {categories.map((category) => (
                  <CategoryFilter
                    key={category.categoryGuid}
                    item={category}
                    activeQuery={query}
                    categoryRoot={categoryRoot}
                    onClickCategory={setNewQueryValue}
                  />
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      <div className="row">
        <div className="sm:col-22 sm:offset-1">
          {(hasError || !items?.length) && (
            <div className="mt-8">
              <Alert
                type={AlertType.Error}
                text={errorMessage}
                textAttributes={{ role: "alert", "aria-live": "assertive" }}
              />
            </div>
          )}

          <ItemsList items={items} iconsList={iconsList} showDates={showDates} />
        </div>
      </div>

      {hasNextItems && (
        <div className="row mt-8">
          <div className="sm:col-22 sm:offset-1">
            <Button
              template="secondary"
              title={getLocalization(localization, "listOfArticlesCategory/button")}
              type="button"
              isLoading={isLoadingMore}
              onClick={loadNextItems}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleList;
